import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useListingStore } from '../stores/listingStore';
import { useAuth } from '../context/AuthContext';
import ListingCard from '../components/ListingCard';
import Map from '../components/Map';
import SearchFilters from '../components/SearchFilters';
import { Building2, Plus } from 'lucide-react';

interface SearchFilters {
  location: string;
  priceRange: string;
  bedrooms: string;
  dateFilter: {
    startDate: string;
    duration: number;
  };
}

const DEFAULT_CENTER = { lat: 40.7128, lng: -74.0060 }; // NYC coordinates

const Listings = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { listings, favorites, toggleFavorite } = useListingStore();
  const { user } = useAuth();
  const [filteredListings, setFilteredListings] = useState<any[]>([]);
  const [selectedListing, setSelectedListing] = useState<string | null>(null);
  const [searchFilters, setSearchFilters] = useState<SearchFilters>({
    location: searchParams.get('location') || '',
    priceRange: '',
    bedrooms: '',
    dateFilter: {
      startDate: '',
      duration: 0
    }
  });

  // Filter out user's own listings, leased listings, and listings with overlapping dates
  const availableListings = useMemo(() => {
    return listings.filter(listing => {
      // Not user's own listing
      if (listing.ownerId === user?.id) return false;
      
      // Only active listings
      if (listing.status !== 'active') return false;
      
      // Check if listing is already leased for the requested dates
      if (listing.isLeased) {
        const listingStart = new Date(listing.available);
        const listingEnd = new Date(listing.endDate);
        
        // If searching for specific dates
        if (searchFilters.dateFilter.startDate) {
          const searchStart = new Date(searchFilters.dateFilter.startDate);
          let searchEnd;
          
          if (searchFilters.dateFilter.duration) {
            searchEnd = new Date(searchStart);
            searchEnd.setMonth(searchEnd.getMonth() + searchFilters.dateFilter.duration);
          } else {
            searchEnd = new Date(listingEnd);
          }
          
          // Check for date overlap
          if (!(listingEnd < searchStart || listingStart > searchEnd)) {
            return false;
          }
        }
      }
      
      return true;
    });
  }, [listings, user?.id, searchFilters.dateFilter]);

  // Calculate map center based on filtered listings
  const mapCenter = useMemo(() => {
    if (!filteredListings.length) {
      return DEFAULT_CENTER;
    }

    // Filter out listings without valid coordinates
    const validListings = filteredListings.filter(
      listing => listing.coordinates?.lat && listing.coordinates?.lng
    );

    if (!validListings.length) {
      return DEFAULT_CENTER;
    }

    // Calculate average coordinates
    const sumCoords = validListings.reduce(
      (acc, listing) => ({
        lat: acc.lat + listing.coordinates.lat,
        lng: acc.lng + listing.coordinates.lng
      }),
      { lat: 0, lng: 0 }
    );

    return {
      lat: sumCoords.lat / validListings.length,
      lng: sumCoords.lng / validListings.length
    };
  }, [filteredListings]);

  useEffect(() => {
    const locationParam = searchParams.get('location');
    if (locationParam) {
      setSearchFilters(prev => ({ ...prev, location: locationParam }));
      handleSearch(null, locationParam);
    } else {
      setFilteredListings(availableListings);
    }
  }, [searchParams, availableListings]);

  const handleSearch = (e: React.FormEvent | null, locationOverride?: string) => {
    if (e) e.preventDefault();
    
    const searchLocation = locationOverride || searchFilters.location;
    const filtered = availableListings.filter(listing => {
      // Location match - search by area/neighborhood
      const locationMatch = !searchLocation || 
        listing.displayLocation?.toLowerCase().includes(searchLocation.toLowerCase()) ||
        listing.location.toLowerCase().includes(searchLocation.toLowerCase());

      // Bedroom match
      const bedroomMatch = !searchFilters.bedrooms || 
        listing.beds.toString() === searchFilters.bedrooms;

      // Price match
      let priceMatch = true;
      if (searchFilters.priceRange) {
        const [min, max] = searchFilters.priceRange.split('-').map(p => 
          p === '+' ? Infinity : parseInt(p.replace(/\D/g, ''))
        );
        priceMatch = listing.price >= min && (max === Infinity || listing.price <= max);
      }

      // Date match
      let dateMatch = true;
      if (searchFilters.dateFilter.startDate) {
        const listingStartDate = new Date(listing.available);
        const listingEndDate = new Date(listing.endDate);
        const requestedStartDate = new Date(searchFilters.dateFilter.startDate);
        
        if (searchFilters.dateFilter.duration === 0) {
          dateMatch = listingStartDate <= requestedStartDate;
        } else {
          const requestedEndDate = new Date(requestedStartDate);
          requestedEndDate.setMonth(requestedEndDate.getMonth() + searchFilters.dateFilter.duration);
          dateMatch = listingStartDate <= requestedStartDate && listingEndDate >= requestedEndDate;
        }
      }

      return locationMatch && bedroomMatch && priceMatch && dateMatch;
    });

    setFilteredListings(filtered);
    setSelectedListing(null);
  };

  // Only show listings with valid coordinates on the map
  const mappableListings = filteredListings.filter(
    listing => listing.coordinates?.lat && listing.coordinates?.lng
  );

  const EmptyState = () => (
    <div className="flex flex-col items-center justify-center py-12 px-4">
      <div className="w-48 h-48 mb-8">
        <svg width="100%" height="100%" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect width="200" height="200" rx="100" fill="#F3F4F6"/>
          <rect x="60" y="60" width="80" height="100" rx="8" fill="#6366F1"/>
          <rect x="75" y="80" width="50" height="8" rx="4" fill="white"/>
          <rect x="75" y="100" width="40" height="8" rx="4" fill="#E0E7FF"/>
          <rect x="75" y="120" width="30" height="8" rx="4" fill="#E0E7FF"/>
          <path d="M100 40L120 70H80L100 40Z" fill="#4F46E5"/>
          <circle cx="150" cy="150" r="30" fill="#4F46E5"/>
          <path d="M150 135V165M135 150H165" stroke="white" strokeWidth="4" strokeLinecap="round"/>
        </svg>
      </div>
      <h3 className="text-xl font-semibold text-gray-900 mb-2">No Listings Available</h3>
      <p className="text-gray-600 text-center mb-6 max-w-md">
        {searchFilters.location 
          ? "No properties found matching your search criteria. Try adjusting your filters or be the first to list in this area!"
          : "Be the first to list your property and help others find their perfect temporary home."}
      </p>
      <div className="flex gap-4">
        <button
          onClick={() => {
            setSearchFilters({
              location: '',
              priceRange: '',
              bedrooms: '',
              dateFilter: { startDate: '', duration: 0 }
            });
            handleSearch(null);
          }}
          className="px-4 py-2 text-gray-700 hover:text-gray-900"
        >
          Clear Filters
        </button>
        <Link
          to="/list-property"
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
        >
          <Plus className="h-5 w-5 mr-2" />
          Add Property
        </Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <SearchFilters
        filters={searchFilters}
        onFiltersChange={setSearchFilters}
        onSearch={handleSearch}
        onLocationSelect={(location) => {
          setSearchFilters(prev => ({ ...prev, location: location.name }));
          handleSearch(null, location.name);
        }}
      />

      <div className="flex flex-col md:flex-row h-[calc(100vh-64px)]">
        {/* Listings Section */}
        <div className="w-full md:w-2/3 p-4 overflow-y-auto">
          <div className="mb-4">
            <h2 className="text-xl font-semibold">
              {filteredListings.length} Available Listings
            </h2>
          </div>

          {filteredListings.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {filteredListings.map((listing) => (
                <ListingCard
                  key={listing.id}
                  listing={listing}
                  onFavorite={() => toggleFavorite(listing.id)}
                  isFavorite={favorites.includes(listing.id)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Map Section */}
        <div className="w-full md:w-1/3 h-[400px] md:h-full">
          <div className="sticky top-0 h-full">
            <Map 
              listings={mappableListings}
              center={mapCenter}
              onMarkerClick={(listing) => setSelectedListing(listing.id)}
              selectedListing={selectedListing}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Listings;